// src/Hooks/useUser.ts

import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { userService } from "../Api/services/userService";
import { User } from "../Types/UserManagement";

export const useUser = () => {
  const [user, setUser] = useState<User>();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleError = (err: unknown, defaultMessage: string) => {
    if (err instanceof AxiosError) {
      const errorResponse = err.response?.data as {
        message?: string;
        errors?: {
          issues?: Array<{
            code: string;
            expected: string;
            received: string;
            path: string[];
            message: string;
          }>;
          name?: string;
        };
        statusCode?: number;
      };

      if (
        errorResponse?.errors?.name === "ZodError" &&
        errorResponse.errors.issues
      ) {
        const zodErrorMessage = errorResponse.errors.issues
          .map(
            (issue) => `Field ${issue.path.join(".")} error: ${issue.message}`
          )
          .join(", ");

        setError(`Validation Error: ${zodErrorMessage}`);
      } else {
        setError(errorResponse?.message || err.message || defaultMessage);
      }
    } else {
      setError(defaultMessage);
    }
  };

  const getUserById = async (id: string) => {
    setLoading(true);
    try {
      const response = await userService.getUserById(id);
      setUser(response.data.data);
      setError(null);
    } catch (err) {
      handleError(err, "Failed to fetch clients");
    } finally {
      setLoading(false);
      return error;
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await userService.getUsers();
      setUsers(response.data.data);
      setError(null);
    } catch (err) {
      handleError(err, "Failed to fetch clients");
    } finally {
      setLoading(false);
      return error;
    }
  };

  const deleteUser = async (id: string) => {
    setLoading(true);
    try {
      await userService.deleteUser(id);
      const response = await userService.getUsers();
      setUsers(response.data.data);
      setError(null);
    } catch (err) {
      handleError(err, "Failed to fetch clients");
    } finally {
      setLoading(false);
      return error;
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  return {
    user,
    users,
    loading,
    error,
    getUserById,
    deleteUser,
  };
};
